/**
 * Read-only list pages for tracking admin (scan élèves, trip stops, positions, etc.).
 * Each page uses useTrackingAdminList + TrackingApiListPage with entity-specific columns.
 */
import { useMemo } from "react";
import { createColumnHelper } from "@tanstack/react-table";
import TrackingApiListPage, { dateColumn, textColumn } from "./TrackingApiListPage";
import { useTrackingAdminList } from "hooks/useTrackingAdminList";
import {
  fetchTripStudentsCan,
  fetchTripStops,
  fetchTripPositions,
  fetchStudentPresence,
  fetchPatrolChecks,
  fetchIncidentMedia,
  type TripStudentScanRow,
  type TripStopRow,
  type TripPositionRow,
  type StudentPresenceRow,
  type PatrolCheckRow,
  type IncidentMediaRow,
} from "services/tracking/trackingAdminService";
import { fetchGuardPatrols, fetchGuardAgents, type GuardPatrol, type GuardAgentOption } from "services/tracking/guardService";

// Trip students can
export function TrackingTripStudentsCanPage() {
  const { data, isLoading, error } = useTrackingAdminList("trip-students-can", fetchTripStudentsCan);
  const col = createColumnHelper<TripStudentScanRow>();
  const columns = useMemo(
    () => [
      col.accessor("scan_type", { header: "Type", cell: (c) => <span>{c.getValue()}</span> }),
      textColumn<TripStudentScanRow>("trip_id", "Trajet", (r) => r.trip_id?.slice(0, 8)),
      col.accessor("student_id", { header: "Élève (ID)", cell: (c) => <span>{c.getValue()}</span> }),
      dateColumn<TripStudentScanRow>("scanned_at", "Scanné le", (r) => r.scanned_at),
    ],
    []
  );
  return (
    <TrackingApiListPage
      title="Scan des élèves"
      data={data}
      isLoading={isLoading}
      error={error}
      columns={columns}
      searchPlaceholder="Rechercher (trajet, élève, type)"
      getSearchFilter={(row, q) =>
        (row.trip_id && row.trip_id.toLowerCase().includes(q)) ||
        String(row.student_id).toLowerCase().includes(q) ||
        (row.scan_type && row.scan_type.toLowerCase().includes(q))
      }
    />
  );
}

// Trip stops
export function TrackingTripStopsPage() {
  const { data, isLoading, error } = useTrackingAdminList("trip-stops", fetchTripStops);
  const col = createColumnHelper<TripStopRow>();
  const columns = useMemo(
    () => [
      textColumn<TripStopRow>("trip_id", "Trajet", (r) => r.trip_id?.slice(0, 8)),
      col.accessor("order", { header: "Ordre", cell: (c) => <span>{c.getValue()}</span> }),
      textColumn<TripStopRow>("label", "Libellé", (r) => r.label),
      col.accessor("is_locked", { header: "Verrouillé", cell: (c) => <span>{c.getValue() ? "Oui" : "Non"}</span> }),
    ],
    []
  );
  return (
    <TrackingApiListPage
      title="Stops des trajets"
      data={data}
      isLoading={isLoading}
      error={error}
      columns={columns}
      searchPlaceholder="Rechercher (trajet, libellé)"
      getSearchFilter={(row, q) =>
        (row.trip_id && row.trip_id.toLowerCase().includes(q)) ||
        (row.label && row.label.toLowerCase().includes(q)) ||
        String(row.order).toLowerCase().includes(q)
      }
    />
  );
}

// Trip positions
export function TrackingTripPositionsPage() {
  const { data, isLoading, error } = useTrackingAdminList("trip-positions", fetchTripPositions);
  const col = createColumnHelper<TripPositionRow>();
  const columns = useMemo(
    () => [
      textColumn<TripPositionRow>("trip_id", "Trajet", (r) => r.trip_id?.slice(0, 8)),
      col.accessor("latitude", { header: "Lat.", cell: (c) => <span>{c.getValue()}</span> }),
      col.accessor("longitude", { header: "Long.", cell: (c) => <span>{c.getValue()}</span> }),
      dateColumn<TripPositionRow>("recorded_at", "Enregistré", (r) => r.recorded_at),
    ],
    []
  );
  return (
    <TrackingApiListPage
      title="Positions des trajets"
      data={data}
      isLoading={isLoading}
      error={error}
      columns={columns}
      searchPlaceholder="Rechercher (trajet, lat/long)"
      getSearchFilter={(row, q) =>
        (row.trip_id && row.trip_id.toLowerCase().includes(q)) ||
        (row.latitude && row.latitude.toLowerCase().includes(q)) ||
        (row.longitude && row.longitude.toLowerCase().includes(q))
      }
    />
  );
}

// Student presence
export function TrackingStudentPresencePage() {
  const { data, isLoading, error } = useTrackingAdminList("student-presence", fetchStudentPresence);
  const col = createColumnHelper<StudentPresenceRow>();
  const columns = useMemo(
    () => [
      textColumn<StudentPresenceRow>("trip_id", "Trajet", (r) => r.trip_id?.slice(0, 8)),
      col.accessor("student_id", { header: "Élève (ID)", cell: (c) => <span>{c.getValue()}</span> }),
      col.accessor("status", { header: "Statut", cell: (c) => <span>{c.getValue()}</span> }),
      dateColumn<StudentPresenceRow>("scanned_at", "Scanné", (r) => r.scanned_at),
    ],
    []
  );
  return (
    <TrackingApiListPage
      title="Présence des élèves"
      data={data}
      isLoading={isLoading}
      error={error}
      columns={columns}
      searchPlaceholder="Rechercher (trajet, élève, statut)"
      getSearchFilter={(row, q) =>
        (row.trip_id && row.trip_id.toLowerCase().includes(q)) ||
        String(row.student_id).toLowerCase().includes(q) ||
        (row.status && row.status.toLowerCase().includes(q))
      }
    />
  );
}

// Patrols (guard)
export function TrackingPatrolsPage() {
  const { data, isLoading, error } = useTrackingAdminList("guard-patrols", fetchGuardPatrols);
  const col = createColumnHelper<GuardPatrol>();
  const columns = useMemo(
    () => [
      col.accessor("name", { header: "Nom", cell: (c) => <span>{c.getValue() ?? "—"}</span> }),
      col.accessor("status", { header: "Statut", cell: (c) => <span>{c.getValue() ?? "—"}</span> }),
      col.accessor("site_name", { header: "Site", cell: (c) => <span>{c.getValue() ?? "—"}</span> }),
      col.accessor("started_at", { header: "Démarré", cell: (c) => <span>{c.getValue() ? new Date(c.getValue() as string).toLocaleString("fr-FR") : "—"}</span> }),
    ],
    []
  );
  return (
    <TrackingApiListPage
      title="Patrouilles"
      data={data}
      isLoading={isLoading}
      error={error}
      columns={columns}
      searchPlaceholder="Rechercher (nom, statut, site)"
      getSearchFilter={(row, q) =>
        (row.name && row.name.toLowerCase().includes(q)) ||
        (row.status && row.status.toLowerCase().includes(q)) ||
        (row.site_name && row.site_name.toLowerCase().includes(q))
      }
    />
  );
}

// Incident media
export function TrackingIncidentMediaPage() {
  const { data, isLoading, error } = useTrackingAdminList("incident-media", fetchIncidentMedia);
  const col = createColumnHelper<IncidentMediaRow>();
  const columns = useMemo(
    () => [
      textColumn<IncidentMediaRow>("incident_id", "Incident", (r) => r.incident_id?.slice(0, 8)),
      col.accessor("media_type", { header: "Type", cell: (c) => <span>{c.getValue()}</span> }),
      col.accessor("order", { header: "Ordre", cell: (c) => <span>{c.getValue()}</span> }),
      dateColumn<IncidentMediaRow>("created_at", "Créé", (r) => r.created_at),
    ],
    []
  );
  return (
    <TrackingApiListPage
      title="Media des incidents"
      data={data}
      isLoading={isLoading}
      error={error}
      columns={columns}
      searchPlaceholder="Rechercher (incident, type)"
      getSearchFilter={(row, q) =>
        (row.incident_id && row.incident_id.toLowerCase().includes(q)) ||
        (row.media_type && row.media_type.toLowerCase().includes(q))
      }
    />
  );
}

// Guard agents
export function TrackingGuardAgentsPage() {
  const { data, isLoading, error } = useTrackingAdminList("guard-agents", fetchGuardAgents);
  const col = createColumnHelper<GuardAgentOption>();
  const columns = useMemo(
    () => [
      col.accessor("id", { header: "ID", cell: (c) => <span>{String(c.getValue()).slice(0, 8)}…</span> }),
      col.accessor("name", { header: "Nom", cell: (c) => <span>{c.getValue() ?? "—"}</span> }),
    ],
    []
  );
  return (
    <TrackingApiListPage
      title="Gardes"
      data={data}
      isLoading={isLoading}
      error={error}
      columns={columns}
      searchPlaceholder="Rechercher (nom, ID)"
      getSearchFilter={(row, q) =>
        (row.name && row.name.toLowerCase().includes(q)) ||
        (row.id && String(row.id).toLowerCase().includes(q))
      }
    />
  );
}

// Patrol checks
export function TrackingPatrolChecksPage() {
  const { data, isLoading, error } = useTrackingAdminList("patrol-checks", fetchPatrolChecks);
  const col = createColumnHelper<PatrolCheckRow>();
  const columns = useMemo(
    () => [
      textColumn<PatrolCheckRow>("patrol_id", "Ronde", (r) => r.patrol_id?.slice(0, 8)),
      textColumn<PatrolCheckRow>("checkpoint_id", "Point", (r) => r.checkpoint_id?.slice(0, 8)),
      dateColumn<PatrolCheckRow>("checked_at", "Heure", (r) => r.checked_at),
      col.accessor("agent_present", { header: "Présent", cell: (c) => <span>{c.getValue() ? "Oui" : "Non"}</span> }),
    ],
    []
  );
  return (
    <TrackingApiListPage
      title="Checks"
      data={data}
      isLoading={isLoading}
      error={error}
      columns={columns}
      searchPlaceholder="Rechercher (ronde, point)"
      getSearchFilter={(row, q) =>
        (row.patrol_id && row.patrol_id.toLowerCase().includes(q)) ||
        (row.checkpoint_id && row.checkpoint_id.toLowerCase().includes(q))
      }
    />
  );
}
